/**
 * SieveCaptureCard — the Data-entry surface for an AFS sieve test.
 *
 * Lists the sieve-shaker equipment assigned to the operator (any manual-entry
 * target carrying a recognisable mesh grid), renders the branded sieve sheet with
 * the mass grid pre-seeded to 0, and on Save publishes the 12 mesh masses through
 * the ordinary readings ingress. GFN is NOT published — the shaker's GFN computed
 * tag derives it from those masses. Mirrors the Equipment tab's publish flow.
 */

import { useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { FlaskConical } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/lib/ui/card";
import { Button } from "@/lib/ui/button";
import { EmptyState } from "@/lib/ui/empty-state";
import { useToast } from "@/lib/ui/toast";
import { useOrg } from "@/lib/org-context";
import { useTenantTheme } from "@/lib/tenant-theme";
import { myManualEntry, thingReadings, type ManualEntryTarget } from "@/lib/api";
import { AFS_US_SERIES } from "@/lib/qc/gfn";
import { computeSieveSheet } from "@/lib/qc/sieve-sheet";
import { buildSieveReadings, hasSieveFields, mapSieveFields } from "@/lib/qc/sieve-capture";
import { SieveSheetView } from "@/components/qc/SieveSheetView";

/** A mass grid seeded to "0" for every mesh, so every save carries all 12. */
function zeroSeed(): Record<string, string> {
  const seed: Record<string, string> = {};
  for (const s of AFS_US_SERIES) seed[s.meshDesignation] = "0";
  return seed;
}

export function SieveCaptureCard() {
  const targetsQuery = useQuery({
    queryKey: ["my-manual-entry-targets"],
    queryFn: () => myManualEntry.targets(),
    staleTime: 30_000,
  });

  const sieveTargets = useMemo(
    () => (targetsQuery.data?.items ?? []).filter((t) => hasSieveFields(t.fields)),
    [targetsQuery.data],
  );

  const [entityId, setEntityId] = useState<string>("");
  const selected =
    sieveTargets.find((t) => t.entity_id === entityId) ?? sieveTargets[0];

  if (targetsQuery.isLoading) {
    return <p className="text-sm text-on-surface-muted">Loading your sieve shakers…</p>;
  }

  if (sieveTargets.length === 0) {
    return (
      <EmptyState
        icon={FlaskConical}
        title="No sieve shaker assigned to you"
        description="Add a Sieve Shaker to the plant (Twin → Process graph → Add equipment), assign yourself as its operator, and its AFS mesh sheet appears here."
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <FlaskConical className="size-4 text-on-surface-muted" />
        <h2 className="text-sm font-semibold text-on-surface">Sieve analysis (AFS grain fineness)</h2>
        <span className="text-xs text-on-surface-muted">
          key the mass retained on each mesh — GFN is computed automatically
        </span>
        {sieveTargets.length > 1 && (
          <select
            className="ml-auto h-9 rounded-md border border-border bg-surface px-2 text-sm text-on-surface"
            value={selected?.entity_id ?? ""}
            onChange={(e) => setEntityId(e.target.value)}
          >
            {sieveTargets.map((t) => (
              <option key={t.entity_id} value={t.entity_id}>
                {t.entity_name}
              </option>
            ))}
          </select>
        )}
      </div>

      {selected && <SieveCaptureForm key={selected.entity_id} target={selected} />}
    </div>
  );
}

function SieveCaptureForm({ target }: { target: ManualEntryTarget }) {
  const toast = useToast();
  const { activeOrg } = useOrg();
  const theme = useTenantTheme();

  const [masses, setMasses] = useState<Record<string, string>>(zeroSeed);

  const fieldMap = useMemo(() => mapSieveFields(target.fields), [target.fields]);

  // Positive masses only feed the sheet's GFN math; the zeros still publish.
  const retained = useMemo(() => {
    const out: Record<string, number> = {};
    for (const [mesh, raw] of Object.entries(masses)) {
      const n = Number(raw);
      if (Number.isFinite(n) && n > 0) out[mesh] = n;
    }
    return out;
  }, [masses]);

  const sheet = useMemo(() => computeSieveSheet(AFS_US_SERIES, retained), [retained]);
  const hasData = sheet.totalMass > 0;

  const save = useMutation({
    mutationFn: () =>
      thingReadings.publish(target.thing_id, {
        readings: buildSieveReadings(fieldMap, retained),
      }),
    onSuccess: (r) => {
      toast.push(
        "ok",
        `Saved sieve test for ${target.entity_name} — GFN ${sheet.gfn.toFixed(1)} (${r.accepted} masses logged). The report + GFN trend update once it recomputes.`,
      );
      setMasses(zeroSeed());
    },
    onError: () => toast.push("error", "Couldn't save the sieve test."),
  });

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
        <CardTitle className="flex items-center gap-2">{target.entity_name}</CardTitle>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setMasses(zeroSeed())}
            className="text-xs text-brand hover:underline"
          >
            Reset
          </button>
          <Button size="sm" onClick={() => save.mutate()} disabled={!hasData || save.isPending}>
            {save.isPending ? "Saving…" : "Save sieve test"}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <SieveSheetView
          sheet={sheet}
          masses={masses}
          onMass={(mesh, value) => setMasses((prev) => ({ ...prev, [mesh]: value }))}
          reportedGfn=""
          orgName={activeOrg?.orgName ?? "Organisation"}
          logoUrl={theme.logoUrl}
          subjectName={target.entity_name}
          showCrossCheck={false}
        />
      </CardContent>
    </Card>
  );
}
